import { CircularProgress } from "@mui/material"
import { useEffect, useState } from "react"
import { Button, Col, Form, InputGroup, Modal, Row } from "react-bootstrap"
import { API_ENDPOINTS } from "../../../api/endpoints"
import { useApi } from "../../../hooks/useApi"
import {
  EditEgresadoModalProps,
  IEgresados,
} from "../../../interfaces/IEgresados"
import { IEventos } from "../../../interfaces/IEventos"
import { CategoriaInvitado, TipoEvento } from "../../../interfaces/TipoEvento"
import {
  formatDateGuiones,
  hasEventPassed,
  sortEventosByDate,
} from "../../../utils/dateUtils"
import { validateDniFormat, validateEmail } from "../../../utils/validation"
import { getFieldConfig, labelInvitado } from "../../../utils/tipoEventoTheme"
import SpinnerOverlay from "../../SpinnerOverlay/SpinnerOverlay"
import "./EditEgresadoModal.css"

interface DniValidationResponse {
  dniExistentes: number[]
}

const EditEgresadoModal: React.FC<EditEgresadoModalProps> = ({
  show,
  handleClose,
  handleEgresadoEdit,
  selectedEgresado,
  idEvento,
  tipoEvento,
}) => {
  const cfg = getFieldConfig(tipoEvento, selectedEgresado?.categoria ?? CategoriaInvitado.CENA)
  const labelTitulo = labelInvitado(tipoEvento)
  const [nombre, setNombre] = useState("")
  const [apellido, setApellido] = useState("")
  const [dni, setDni] = useState("")
  const [clave, setClave] = useState("")
  const [categoria, setCategoria] = useState<CategoriaInvitado>(CategoriaInvitado.CENA)
  const [loadingRegenerar, setLoadingRegenerar] = useState(false)
  const [tarjetasPagadas, setTarjetasPagadas] = useState("")
  const [tarjetasIngresadas, setTarjetasIngresadas] = useState("")
  const [tarjetasPorIngresar, setTarjetasPorIngresar] = useState("")
  const [tarjetasPagadasMenores, setTarjetasPagadasMenores] = useState("")
  const [tarjetasIngresadasMenores, setTarjetasIngresadasMenores] = useState("")
  const [tarjetasPorIngresarMenores, setTarjetasPorIngresarMenores] =
    useState("")
  const [email, setEmail] = useState("")
  const [telefono, setTelefono] = useState("")
  const [menuOpcional, setMenuOpcional] = useState("")
  const [observaciones, setObservaciones] = useState("")
  const [eventoId, setIdEventoId] = useState(idEvento)
  const [eventoCompletoPorId, setEventoCompletoPorId] = useState<IEventos>()
  const [eventos, setEventos] = useState<IEventos[]>([])
  const [dniValid, setDniValid] = useState(true)
  const [dniEnUso, setDniEnUso] = useState(false)
  const [originalDni, setOriginalDni] = useState("")
  const api = useApi()
  const API_URL = API_ENDPOINTS.EGRESADOS
  const API_URL_EVENTOS = API_ENDPOINTS.EVENTOS
  const [emailValido, setEmailValido] = useState(true)
  const [tarjetasValidacionError, setTarjetasValidacionError] = useState(false)
  const [tarjetasMenoresValidacionError, setTarjetasMenoresValidacionError] =
    useState(false)
  const [loading, setLoading] = useState(false)
  const [loadingDni, setLoadingDni] = useState(false)

  useEffect(() => {
    if (!eventoId) return
    const callApiAndSetEventos = async () => {
      setLoading(true)
      try {
        const data = await api.get<IEventos>(`${API_URL_EVENTOS}/${eventoId}`, {
          showErrorToast: false,
        })
        setEventoCompletoPorId(data)
      } catch {
        // toast suprimido para no spamear al cargar
      } finally {
        setLoading(false)
      }
    }
    callApiAndSetEventos()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventoId])

  useEffect(() => {
    if (selectedEgresado) {
      setNombre(selectedEgresado.nombre)
      setApellido(selectedEgresado.apellido)
      setDni(selectedEgresado.dni?.toString() ?? "")
      setClave(selectedEgresado.clave)
      setCategoria(selectedEgresado.categoria ?? CategoriaInvitado.CENA)
      setTarjetasPagadas(selectedEgresado.tarjetasPagadas.toString())
      setTarjetasIngresadas(
        selectedEgresado.tarjetasIngresadas?.toString() || "0",
      )
      setTarjetasPorIngresar(
        selectedEgresado.tarjetasPorIngresar?.toString() || "0",
      )
      setTarjetasPagadasMenores(
        selectedEgresado.tarjetasPagadasMenores.toString(),
      )
      setTarjetasIngresadasMenores(
        selectedEgresado.tarjetasIngresadasMenores?.toString() || "0",
      )
      setTarjetasPorIngresarMenores(
        selectedEgresado.tarjetasPorIngresarMenores?.toString() || "0",
      )
      setEmail(selectedEgresado.email)
      setTelefono(selectedEgresado.telefono)
      setMenuOpcional(selectedEgresado.menuOpcional ?? "")
      setObservaciones(selectedEgresado.observaciones ?? "")
      const initialEventoId = idEvento || selectedEgresado.evento?.id || ""
      setIdEventoId(initialEventoId.toString())

      setOriginalDni(selectedEgresado.dni?.toString() ?? "")

      const dniStr = selectedEgresado.dni?.toString() ?? ""
      setDniValid(
        tipoEvento !== TipoEvento.EGRESADOS ||
        !cfg.dniRequerido ||
        validateDniFormat(dniStr),
      )
      setDniEnUso(false)
    }

    const callApiAndSetEventos = async () => {
      setLoading(true)
      try {
        const data = await api.get<IEventos[]>(API_URL_EVENTOS, {
          errorMessage: "Error al obtener eventos",
        })
        setEventos(sortEventosByDate(data))
      } catch {
        // toast ya mostrado
      } finally {
        setLoading(false)
      }
    }

    callApiAndSetEventos()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEgresado])

  const validateDNI = async (dni: string): Promise<boolean> => {
    if (tipoEvento !== TipoEvento.EGRESADOS) return false
    if (!cfg.dniRequerido) return false
    setLoadingDni(true)
    try {
      const data = await api.post<DniValidationResponse>(
        `${API_URL}/validarDNI?tipoEvento=${tipoEvento}`,
        [parseInt(dni)],
        { errorMessage: "Error al verificar el DNI" },
      )
      return data.dniExistentes.length > 0
    } catch {
      return false
    } finally {
      setLoadingDni(false)
    }
  }

  const handleDniChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newDni = event.target.value
    setDni(newDni)

    if (tipoEvento !== TipoEvento.EGRESADOS) {
      setDniValid(true)
      setDniEnUso(false)
      return
    }
    if (!cfg.dniRequerido) {
      setDniValid(true)
      setDniEnUso(false)
      return
    }
    if (validateDniFormat(newDni)) {
      setDniValid(true)
      const isInUse = await validateDNI(newDni)
      setDniEnUso(isInUse)
    } else {
      setDniValid(false)
      setDniEnUso(false)
    }
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (cfg.dniMostrar && cfg.dniRequerido && !validateDniFormat(dni)) {
      setDniValid(false)
      return
    }

    if (
      tipoEvento === TipoEvento.EGRESADOS &&
      cfg.dniRequerido &&
      dni &&
      dni !== originalDni
    ) {
      if (await validateDNI(dni)) {
        setDniEnUso(true)
        return
      }
    }

    const tarjetasPagadasNumber = parseInt(tarjetasPagadas)

    if (cfg.emailMostrar && email && !validateEmail(email)) {
      setEmailValido(false)
      return
    }
    setEmailValido(true)

    const tarjetasIngresadasNumber = parseInt(tarjetasIngresadas || "0")
    const tarjetasPorIngresarNumber = parseInt(tarjetasPorIngresar || "0")

    if (
      tarjetasPagadasNumber !==
      tarjetasIngresadasNumber + tarjetasPorIngresarNumber
    ) {
      setTarjetasValidacionError(true)
      return
    } else {
      setTarjetasValidacionError(false)
    }

    //validaciones para tarjetas de Menores
    const tarjetasPagadasMenoresNumber = parseInt(tarjetasPagadasMenores || "0")
    const tarjetasIngresadasMenoresNumber = parseInt(
      tarjetasIngresadasMenores || "0",
    )
    const tarjetasPorIngresarMenoresNumber = parseInt(
      tarjetasPorIngresarMenores || "0",
    )

    if (
      tarjetasPagadasMenoresNumber !==
      tarjetasIngresadasMenoresNumber + tarjetasPorIngresarMenoresNumber
    ) {
      setTarjetasMenoresValidacionError(true)
      return
    } else {
      setTarjetasMenoresValidacionError(false)
    }

    if (selectedEgresado) {
      const updatedEgresado: IEgresados = {
        ...selectedEgresado,
        nombre,
        apellido,
        dni: cfg.dniMostrar && dni ? parseInt(dni) : null,
        clave,
        categoria,
        tarjetasPagadas: parseInt(tarjetasPagadas),
        tarjetasIngresadas: parseInt(tarjetasIngresadas),
        tarjetasPorIngresar: parseInt(tarjetasPorIngresar),
        tarjetasPagadasMenores: parseInt(tarjetasPagadasMenores),
        tarjetasIngresadasMenores: parseInt(tarjetasIngresadasMenores),
        tarjetasPorIngresarMenores: parseInt(tarjetasPorIngresarMenores),
        telefono: cfg.telefonoMostrar ? telefono : "",
        email: cfg.emailMostrar ? email : "",
        menuOpcional,
        observaciones,
        eventoId: eventoId ? parseInt(eventoId) : null,
      }
      handleEgresadoEdit(updatedEgresado)
    }
    handleClose()
  }

  const regenerarClave = async () => {
    if (!selectedEgresado) return
    setLoadingRegenerar(true)
    try {
      const data = await api.put<{ clave: string }>(
        `${API_URL}/${selectedEgresado.id}/regenerar-clave`,
        {},
        { errorMessage: "Error al regenerar la clave" },
      )
      setClave(data.clave)
    } catch {
      // toast ya mostrado
    } finally {
      setLoadingRegenerar(false)
    }
  }

  const resetForm = () => {
    if (selectedEgresado) {
      setNombre(selectedEgresado.nombre)
      setApellido(selectedEgresado.apellido)
      setDni(selectedEgresado.dni?.toString() ?? "")
      setClave(selectedEgresado.clave)
      setTarjetasPagadas(selectedEgresado.tarjetasPagadas.toString())
      setTarjetasIngresadas(
        selectedEgresado.tarjetasIngresadas?.toString() || "0",
      )
      setTarjetasPorIngresar(
        selectedEgresado.tarjetasPorIngresar?.toString() || "0",
      )
      setIdEventoId(
        selectedEgresado.eventoId ? selectedEgresado.eventoId.toString() : "",
      )
    }
    setDniValid(true)
    setDniEnUso(false)
    handleClose()
  }

  const handleCancelar = () => {
    resetForm()
    handleClose()
  }

  useEffect(() => {
    if (email) {
      setEmailValido(validateEmail(email))
    }
  }, [email])

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      dialogClassName="egresado-edit-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>Editar {labelTitulo}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {loading && <SpinnerOverlay />}
          <Row>
            <Col md={6}>
              <Form.Group controlId="formNombre">
                <Form.Label>Nombre</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Ingrese nombre"
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="formApellido">
                <Form.Label>Apellido</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Ingrese apellido"
                  value={apellido}
                  onChange={(e) => setApellido(e.target.value)}
                  required
                />
              </Form.Group>
            </Col>
          </Row>
          {cfg.dniMostrar && (
            <Row>
              <Col md={6}>
                <Form.Group controlId="formDni">
                  <Form.Label>DNI{!cfg.dniRequerido && " (opcional)"}</Form.Label>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <Form.Control
                      type="number"
                      placeholder="Ingrese DNI"
                      value={dni}
                      onChange={handleDniChange}
                      required={cfg.dniRequerido}
                      isInvalid={!dniValid || dniEnUso}
                    />
                    {loadingDni && (
                      <div style={{ width: 24, height: 24, display: "flex", alignItems: "center", marginLeft: 10 }}>
                        <CircularProgress size={24} />
                      </div>
                    )}
                  </div>
                  {!dniValid && <Form.Text className="text-danger">El DNI debe tener exactamente 8 caracteres.</Form.Text>}
                  {dniEnUso && <Form.Text className="text-danger">Este DNI ya está en uso.</Form.Text>}
                </Form.Group>
              </Col>
              {cfg.telefonoMostrar && (
                <Col md={6}>
                  <Form.Group controlId="formTelefono">
                    <Form.Label>Teléfono</Form.Label>
                    <Form.Control type="text" placeholder="Ingrese teléfono" value={telefono} onChange={(e) => setTelefono(e.target.value)} />
                  </Form.Group>
                </Col>
              )}
            </Row>
          )}

          {!cfg.dniMostrar && cfg.telefonoMostrar && (
            <Row>
              <Col md={6}>
                <Form.Group className="mt-2" controlId="formTelefono">
                  <Form.Label>Teléfono</Form.Label>
                  <Form.Control type="text" placeholder="Ingrese teléfono" value={telefono} onChange={(e) => setTelefono(e.target.value)} />
                </Form.Group>
              </Col>
            </Row>
          )}

          {cfg.emailMostrar && (
            <Row>
              <Col md={6}>
                <Form.Group controlId="formEmail">
                  <Form.Label>Email</Form.Label>
                  <Form.Control type="email" placeholder="Ingrese email" value={email} onChange={(e) => setEmail(e.target.value)} isInvalid={email !== "" && !emailValido} />
                  <Form.Control.Feedback type="invalid">Email no válido.</Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="formClave">
                  <Form.Label>Clave</Form.Label>
                  <InputGroup>
                    <Form.Control type="text" value={clave} readOnly style={{ backgroundColor: "#f8f9fa", fontFamily: "monospace" }} />
                    <Button variant="outline-secondary" onClick={regenerarClave} disabled={loadingRegenerar}>
                      {loadingRegenerar ? <CircularProgress size={16} /> : <i className="bi bi-arrow-clockwise" />}
                    </Button>
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>
          )}

          {!cfg.emailMostrar && (
            <Row>
              <Col md={6}>
                <Form.Group className="mt-2" controlId="formClave">
                  <Form.Label>Clave</Form.Label>
                  <InputGroup>
                    <Form.Control type="text" value={clave} readOnly style={{ backgroundColor: "#f8f9fa", fontFamily: "monospace" }} />
                    <Button variant="outline-secondary" onClick={regenerarClave} disabled={loadingRegenerar}>
                      {loadingRegenerar ? <CircularProgress size={16} /> : <i className="bi bi-arrow-clockwise" />}
                    </Button>
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>
          )}


          {cfg.tarjetasMayores && (
            <>
              <Row>
                <Col md={4}>
                  <Form.Group className="mt-2" controlId="formTarjetasPagadas">
                    <Form.Label style={{ marginBottom: "2px" }}>Tarjetas Pagadas</Form.Label>
                    <Form.Control type="number" placeholder="Ingrese tarjetas pagadas" value={tarjetasPagadas} onChange={(e) => setTarjetasPagadas(e.target.value)} required />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mt-2" controlId="formTarjetasIngresadas">
                    <Form.Label style={{ color: "#e95133", marginBottom: "2px" }}>Tarjetas Ingresadas</Form.Label>
                    <Form.Control type="number" placeholder="Ingrese tarjetas ingresadas" value={tarjetasIngresadas} onChange={(e) => setTarjetasIngresadas(e.target.value)} />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mt-2" controlId="formTarjetasPorIngresar">
                    <Form.Label style={{ color: "#009933", marginBottom: "2px" }}>Tarjetas por Ingresar</Form.Label>
                    <Form.Control type="number" placeholder="Ingrese tarjetas por ingresar" value={tarjetasPorIngresar} onChange={(e) => setTarjetasPorIngresar(e.target.value)} />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={4}>
                  <Form.Group className="mt-2" controlId="formTarjetasPagadasMenores">
                    <Form.Label style={{ marginBottom: "2px" }}>Tarjetas Pagadas Menores</Form.Label>
                    <Form.Control type="number" placeholder="Ingrese tarjetas pagadas Menores" value={tarjetasPagadasMenores} onChange={(e) => setTarjetasPagadasMenores(e.target.value)} required />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mt-2" controlId="formTarjetasIngresadasMenores">
                    <Form.Label style={{ color: "#e95133", marginBottom: "2px" }}>Tarjetas Ingresadas Menores</Form.Label>
                    <Form.Control type="number" placeholder="Ingrese tarjetas ingresadas Menores" value={tarjetasIngresadasMenores} onChange={(e) => setTarjetasIngresadasMenores(e.target.value)} />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mt-2" controlId="formTarjetasPorIngresarMenores">
                    <Form.Label style={{ color: "#009933", marginBottom: "2px" }}>Tarjetas por Ingresar Menores</Form.Label>
                    <Form.Control type="number" placeholder="Ingrese tarjetas por ingresar Menores" value={tarjetasPorIngresarMenores} onChange={(e) => setTarjetasPorIngresarMenores(e.target.value)} />
                  </Form.Group>
                </Col>
              </Row>
            </>
          )}

          {cfg.tarjetasBaile && (
            <Row>
              <Col md={4}>
                <Form.Group className="mt-2" controlId="formTarjetasBaile">
                  <Form.Label style={{ marginBottom: "2px" }}>Tarjetas Baile</Form.Label>
                  <Form.Control type="number" placeholder="Ingrese tarjetas baile" value={tarjetasPagadas} onChange={(e) => setTarjetasPagadas(e.target.value)} required />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mt-2" controlId="formTarjetasIngresadasBaile">
                  <Form.Label style={{ color: "#e95133", marginBottom: "2px" }}>Ingresadas</Form.Label>
                  <Form.Control type="number" placeholder="Ingresadas" value={tarjetasIngresadas} onChange={(e) => setTarjetasIngresadas(e.target.value)} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mt-2" controlId="formTarjetasPorIngresarBaile">
                  <Form.Label style={{ color: "#009933", marginBottom: "2px" }}>Por Ingresar</Form.Label>
                  <Form.Control type="number" placeholder="Por ingresar" value={tarjetasPorIngresar} onChange={(e) => setTarjetasPorIngresar(e.target.value)} />
                </Form.Group>
              </Col>
            </Row>
          )}

          {cfg.menuMesa && (
            <Row>
              <Col md="6">
                <Form.Group className="mb-3 mt-2" controlId="formMenuOpcional">
                  <Form.Label>Menú Opcional</Form.Label>
                  <Form.Control as="textarea" placeholder="Ingrese Menú Opcional" value={menuOpcional} onChange={(e) => setMenuOpcional(e.target.value)} style={{ resize: "none" }} />
                </Form.Group>
              </Col>
              <Col md="6">
                <Form.Group className="mb-3 mt-2" controlId="formObservaciones">
                  <Form.Label>Observación/Mesa</Form.Label>
                  <Form.Control as="textarea" placeholder="Ingrese Observación/Mesa" value={observaciones} onChange={(e) => setObservaciones(e.target.value)} style={{ resize: "none" }} />
                </Form.Group>
              </Col>
            </Row>
          )}

          <Row>
            <Col md={12}>
              <Form.Group controlId="formEvento">
                <Form.Label>Evento</Form.Label>
                <Form.Control
                  as="select"
                  value={eventoId}
                  onChange={(event) => setIdEventoId(event.target.value)}
                  required
                >
                  <option value={eventoCompletoPorId?.id}>
                    {`${eventoCompletoPorId?.nombre} / ${formatDateGuiones(
                      eventoCompletoPorId?.fecha || "",
                    )}`}
                  </option>
                  {eventos
                    .filter((e) => !tipoEvento || !e.tipo || e.tipo === tipoEvento)
                    .map((evento: IEventos) => {
                    const isEventPassed = hasEventPassed(evento.fecha)
                    return (
                      <option
                        key={evento.id}
                        value={evento.id}
                        style={{ color: isEventPassed ? "#a6a8aa" : "black" }}
                      >
                        {`${evento.nombre} / ${formatDateGuiones(
                          evento.fecha,
                        )}`}
                      </option>
                    )
                  })}
                </Form.Control>
              </Form.Group>
            </Col>
          </Row>
          {tarjetasValidacionError && (
            <div className="alert alert-danger" role="alert">
              La suma de tarjetas ingresadas y por ingresar debe ser igual a las
              tarjetas pagadas.
            </div>
          )}
          {tarjetasMenoresValidacionError && (
            <div className="alert alert-danger" role="alert">
              La suma de tarjetas ingresadas de Menores y por ingresar de
              Menores debe ser igual a las tarjetas pagadas de Menores.
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelar}>
            Cancelar
          </Button>
          <Button variant="primary" type="submit">
            Guardar
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}

export default EditEgresadoModal
